import { signal } from '@preact/signals';

// ============================================================================
// Error State - Global error handling for runtime errors
// ============================================================================

export interface AppError {
  readonly message: string;
  readonly stack?: string;
  readonly timestamp: number;
  readonly source?: string;
  readonly line?: number;
  readonly column?: number;
}

// Current error being displayed (null = no error overlay)
export const appError = signal<AppError | null>(null);

// History of recent errors (keep last 10)
export const errorHistory = signal<ReadonlyArray<AppError>>([]);

// Whether to show error history panel
export const showingHistory = signal<boolean>(false);

// ============================================================================
// Actions
// ============================================================================

export const captureError = ({
  message,
  stack,
  source,
  line,
  column,
}: {
  message: string;
  stack?: string;
  source?: string;
  line?: number;
  column?: number;
}): void => {
  const error: AppError = {
    message,
    timestamp: Date.now(),
    ...(stack !== undefined && { stack }),
    ...(source !== undefined && { source }),
    ...(line !== undefined && { line }),
    ...(column !== undefined && { column }),
  };

  appError.value = error;
  errorHistory.value = [...errorHistory.value.slice(-9), error];
};

export const dismissError = (): void => {
  appError.value = null;
  showingHistory.value = false;
};

export const toggleHistory = (): void => {
  showingHistory.value = !showingHistory.value;
};

export const clearErrorHistory = (): void => {
  errorHistory.value = [];
  showingHistory.value = false;
};

// ============================================================================
// Stack Trace Parsing
// ============================================================================

export interface StackFrame {
  readonly functionName: string;
  readonly fileName: string;
  readonly line?: number;
  readonly column?: number;
  readonly raw: string;
}

// Parse stack trace into structured frames
export const parseStackTrace = ({ stack }: { stack: string }): ReadonlyArray<StackFrame> => {
  const lines = stack.split('\n');
  const frames: StackFrame[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('at ')) {
      continue;
    }

    // Parse "at functionName (file:line:column)" or "at file:line:column"
    const withParens = /^at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)$/.exec(trimmed);
    const withoutParens = /^at\s+(.+?):(\d+):(\d+)$/.exec(trimmed);

    if (withParens) {
      frames.push({
        functionName: withParens[1] ?? 'anonymous',
        fileName: extractFileName({ path: withParens[2] ?? '' }),
        line: parseInt(withParens[3] ?? '0', 10),
        column: parseInt(withParens[4] ?? '0', 10),
        raw: trimmed,
      });
    } else if (withoutParens) {
      frames.push({
        functionName: 'anonymous',
        fileName: extractFileName({ path: withoutParens[1] ?? '' }),
        line: parseInt(withoutParens[2] ?? '0', 10),
        column: parseInt(withoutParens[3] ?? '0', 10),
        raw: trimmed,
      });
    } else {
      // Fallback for unparseable lines
      frames.push({
        functionName: 'unknown',
        fileName: trimmed.replace(/^at\s+/, ''),
        raw: trimmed,
      });
    }
  }

  return frames;
};

// Extract just the filename from a full path
const extractFileName = ({ path }: { path: string }): string => {
  // Handle URLs and file paths
  const parts = path.split('/');
  const lastPart = parts[parts.length - 1] ?? path;
  // Remove query strings
  return lastPart.split('?')[0] ?? lastPart;
};
